import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../services/store';
import {
  moveMapToLocation,
  selectCurrentMapCenter,
  showSelectMapPoint,
} from '../../../services/store/modules/app-controls';
import MapPointSelector from '../../containers/MapPointSelector/MapPointSelector';
import PageHolderContent from '../../containers/PageHolderContent/PageHolderContent';
import HeaderBar from '../components/HeaderBar/HeaderBar';
import { back } from '../../shared/vector-images';
import HeaderBarButton from '../components/HeaderBarButton/HeaderBarButton';

interface LatLonValue {
  lat: number;
  lon: number;
}

interface RenderFnProps {
  isLoading: boolean;
  unitsConfig: unknown;
  buttonLabel: string;
  currentMapCenter: LatLonValue;
  handleOnComplete: () => void;
}

interface LocationSelectorLayoutProps {
  instructionText: string;
  isInvalidLocation?: boolean;
  buttonLabel: string;
  heading: string;
  onBack: () => void;
  onSelectLocation: (value: LatLonValue) => void;
  render?: (props: RenderFnProps) => React.ReactNode;

  // This was required for waypoint selection
  editPointRef?: number | undefined;
}

const LocationSelectorLayout: FC<LocationSelectorLayoutProps> = ({
  buttonLabel,
  editPointRef,
  heading,
  instructionText,
  isInvalidLocation,
  render,
  onBack,
  onSelectLocation,
}) => {
  const handleBack = onBack;
  const handleSelectLocation = onSelectLocation;
  const dispatch = useAppDispatch();
  const currentMapCenter = useSelector(selectCurrentMapCenter);
  const handleInitialState = React.useCallback(async () => {
    dispatch(
      await moveMapToLocation({
        currentMapCenter,
      }),
    );
    // don't include center as a dep as it will case the map to constantly move
    // to the users location as they move
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      handleInitialState();
      dispatch(showSelectMapPoint(-1)); // ;(
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, handleInitialState]);

  return (
    <PageHolderContent
      isLoading={false}
      transparentBackground
      errorHeading={isInvalidLocation ? 'Unable to select location' : undefined}
      errorDescription={isInvalidLocation ? 'Please try again.' : undefined}
      headerBarContent={
        <HeaderBar
          solidBackground
          leftNode={
            <HeaderBarButton
              ariaLabel="return"
              icon={back()}
              onClick={handleBack}
            />
          }
          centerNode={heading}
        />
      }
    >
      <MapPointSelector
        onComplete={(value) => handleSelectLocation(value)}
        instructionText={instructionText}
        buttonLabel={buttonLabel}
        render={render}
        updatedUI
        editPointRef={editPointRef}
      />
    </PageHolderContent>
  );
};

LocationSelectorLayout.defaultProps = {
  render: undefined,
  editPointRef: undefined,
  isInvalidLocation: false,
};

export default LocationSelectorLayout;
